import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { Command, EditorState, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { CellSelection, TableMap, deleteColumn, deleteRow, selectedRect } from '@tiptap/pm/tables';

import { TableEdgeSelection, tableEdgeSpan } from './tableEdgeSelection';
import type { CrossedRows } from './tableEdgeSelection';

/** The class prosemirror-tables draws a selected cell with; the stylesheet supplies the look. */
export const SELECTED_CELL_CLASS = 'selectedCell';

export const tableEdgeKey = new PluginKey('flowTableEdge');

/**
 * A caret in the cell now at `row`/`column`, clamped to the table.
 *
 * prosemirror-tables maps a column selection onto the column that slides into its place, so a
 * second Backspace would take that one too. Word leaves a caret, and so does this.
 */
const caretInCell = (tr: Transaction, tablePos: number, row: number, column: number): void => {
	const table = tr.doc.nodeAt(tablePos);
	if (table === null) {
		return;
	}
	const map = TableMap.get(table);
	const index = Math.min(row, map.height - 1) * map.width + Math.min(column, map.width - 1);
	const cellPos = tablePos + 1 + (map.map[index] ?? 0);
	tr.setSelection(TextSelection.near(tr.doc.resolve(cellPos + 1)));
};

const deleteRows: Command = (state, dispatch) => {
	const rect = selectedRect(state);
	return deleteRow(
		state,
		dispatch &&
			((tr: Transaction) => {
				caretInCell(tr, rect.tableStart - 1, rect.top, rect.left);
				dispatch(tr);
			})
	);
};

/** Removes the selected columns and their widths, which the model holds in column lockstep. */
const deleteColumns: Command = (state, dispatch) => {
	const rect = selectedRect(state);
	const tablePos = rect.tableStart - 1;
	const widths: unknown = rect.table.attrs['columnWidthsMm'];
	return deleteColumn(
		state,
		dispatch &&
			((tr: Transaction) => {
				const table = tr.doc.nodeAt(tablePos);
				if (table !== null && Array.isArray(widths) && widths.length === rect.map.width) {
					tr.setNodeMarkup(tablePos, undefined, {
						...table.attrs,
						columnWidthsMm: widths.filter(
							(_, column) => column < rect.left || column >= rect.right
						),
					});
				}
				caretInCell(tr, tablePos, rect.top, rect.left);
				dispatch(tr);
			})
	);
};

/**
 * Word's Backspace on whole rows or whole columns: removes them.
 *
 * Delete still only clears them, as in Word. A selection that is both — the whole table — is
 * left to the table extension, which deletes the table; a block of cells that is neither falls
 * through to clearing, where Word would ask.
 */
export const removeSelectedRowsOrColumns: Command = (state, dispatch) => {
	const { selection } = state;
	if (!(selection instanceof CellSelection)) {
		return false;
	}
	const rows = selection.isRowSelection();
	if (rows === selection.isColSelection()) {
		return false;
	}
	return rows ? deleteRows(state, dispatch) : deleteColumns(state, dispatch);
};

/**
 * Deletes a table-edge selection through its own `replace`.
 *
 * Needed because TipTap's `deleteSelection` command, which the keymap's Backspace and Delete
 * run, deletes `selection.ranges` directly rather than asking the selection to replace itself.
 */
export const deleteTableEdgeSelection: Command = (state, dispatch) => {
	const { selection } = state;
	if (!(selection instanceof TableEdgeSelection) || selection.span === null) {
		return false;
	}
	dispatch?.(state.tr.deleteSelection().scrollIntoView());
	return true;
};

/** Every key the core keymap deletes a selection on. */
const DELETE_KEYS = [
	'Backspace',
	'Mod-Backspace',
	'Shift-Backspace',
	'Alt-Backspace',
	'Delete',
	'Mod-Delete',
	'Alt-Delete',
] as const;

const rowDecorations = (state: EditorState, crossed: CrossedRows): Decoration[] => {
	const table = state.doc.nodeAt(crossed.tablePos);
	if (table === null) {
		return [];
	}
	const decorations: Decoration[] = [];
	let rowPos = crossed.tablePos + 1;
	table.forEach((row, _offset, index) => {
		if (index >= crossed.firstRow && index <= crossed.lastRow) {
			row.forEach((cell, cellOffset) => {
				const cellPos = rowPos + 1 + cellOffset;
				decorations.push(
					Decoration.node(cellPos, cellPos + cell.nodeSize, {
						class: SELECTED_CELL_CLASS,
					})
				);
			});
		}
		rowPos += row.nodeSize;
	});
	return decorations;
};

/** Every row a table-edge selection will take, drawn the way a cell selection is. */
export const edgeDecorations = (state: EditorState): DecorationSet | null => {
	const { selection } = state;
	if (!(selection instanceof TableEdgeSelection)) {
		return null;
	}
	const span = selection.span;
	if (span === null) {
		return null;
	}
	const decorations: Decoration[] = [];
	const covered = (pos: number, end: number): boolean => pos >= span.from && end <= span.to;
	// Tables inside the span go whole, so every one of their cells is marked too.
	state.doc.nodesBetween(span.from, span.to, (node, pos) => {
		if (node.type.spec['tableRole'] === 'table' && covered(pos, pos + node.nodeSize)) {
			decorations.push(
				...rowDecorations(state, {
					tablePos: pos,
					firstRow: 0,
					lastRow: node.childCount - 1,
				})
			);
			return false;
		}
		// Nothing inside a paragraph can hold a table, so there is no reason to walk its text.
		return !node.isTextblock;
	});
	[span.start, span.end].forEach((crossed) => {
		if (crossed !== undefined) {
			decorations.push(...rowDecorations(state, crossed));
		}
	});
	return DecorationSet.create(state.doc, decorations);
};

/** Turns a text selection across a table's edge into a {@link TableEdgeSelection}, and draws it. */
export const tableEdgePlugin = (): Plugin =>
	new Plugin({
		key: tableEdgeKey,
		appendTransaction: (_transactions, _oldState, state) => {
			const { selection } = state;
			if (
				!(selection instanceof TextSelection) ||
				selection instanceof TableEdgeSelection ||
				tableEdgeSpan(selection.$from, selection.$to) === null
			) {
				return null;
			}
			return state.tr.setSelection(
				new TableEdgeSelection(selection.$anchor, selection.$head)
			);
		},
		props: { decorations: edgeDecorations },
	});

/**
 * Table editing the way an analyst coming from Word expects it.
 *
 * A text selection that reaches into a table from outside becomes a {@link TableEdgeSelection},
 * so deleting it takes whole rows instead of tearing the table at a character; and Backspace on
 * whole rows or columns removes them. Priority above the table extension's, whose plugin
 * otherwise answers Backspace on any cell selection by clearing it.
 */
export const FlowTableEditing = Extension.create({
	name: 'flowTableEditing',

	priority: 110,

	addKeyboardShortcuts() {
		const run = (command: Command) => () =>
			command(this.editor.state, this.editor.view.dispatch);
		const deleteEdge = run(deleteTableEdgeSelection);
		const removeRowsOrColumns = run(removeSelectedRowsOrColumns);
		return Object.fromEntries(
			DELETE_KEYS.map((key) => [
				key,
				key === 'Backspace' || key === 'Mod-Backspace'
					? () => removeRowsOrColumns() || deleteEdge()
					: deleteEdge,
			])
		);
	},

	addProseMirrorPlugins() {
		return [tableEdgePlugin()];
	},
});
